// Policy content sourced from altaracore.ai (Privacy Policy, Terms of Use, HITL Governance)
const CONTACT = `**Altara AI**
Digital Trust and AI Governance
Part of the Navigate Compliance Group (Pty) Ltd
Johannesburg, South Africa
Website: [www.altaracore.ai](https://www.altaracore.ai/)`;

const responsibleAi = `Altara AI is built on a single conviction: artificial intelligence must earn the trust of the people it serves. Responsible AI is not a label we use; it is the operating principle that shapes every model, every workflow, every release decision, and every line of policy at Altara.

This page sets out the public commitments we make to our users, our clients, our regulators, and the communities our platforms touch.

## 1. Our Six Commitments

### 1.1 Human Accountability

A human is always accountable for any decision informed by an Altara AI system. AI augments judgment; it does not replace it. Our platforms are designed so that high-impact outputs are reviewable, challengeable, and overrideable, and so that the human accountable for each decision is identifiable.

### 1.2 Transparency

We disclose, in plain language, when our systems use AI. Every NAVI-generated output carries a model version, a prompt version, and a confidence rating. The indicator catalogues, the scoring logic, and the model cards behind our risk assessments are published, not hidden behind marketing copy.

### 1.3 Explainability

Every Altara AI verdict is explainable. We don't ship opaque scores. When NAVI flags a piece of content as a likely scam, the user sees the indicators that triggered, the verbatim evidence from the submission, and the reasoning in plain language. Regulators can audit the methodology, not just the output.

### 1.4 Fairness

We actively test our systems for bias, across languages, demographics, regions, and use cases. We document known limitations. We commit to remediating disparate impact when it is identified, and to disclosing limitations to users before they rely on a result.

### 1.5 Safety and Robustness

We monitor our AI systems continuously for hallucinations, adversarial manipulation, performance degradation, and emerging threats. Our incident-response playbooks include rapid model rollback, indicator-catalogue freeze, and public disclosure where consumer harm is plausible.

### 1.6 Privacy by Design

We collect the minimum information needed to do the job. Identifiers are not requested where they are not necessary. Data residency, encryption, retention limits, and user-rights controls are designed into our systems, not bolted on. See our [Privacy Policy](/privacy-policy) for the full detail.

## 2. The Frameworks We Align With

Our Responsible AI programme is informed by, and continuously cross-walked against, the leading global frameworks:

| Framework | What it gives us |
| --- | --- |
| OECD AI Principles | The international baseline for trustworthy AI |
| NIST AI Risk Management Framework (AI RMF 1.0) | Concrete control families for govern / map / measure / manage |
| ISO/IEC 42001 | A certifiable AI management system standard |
| ISO/IEC 23894 | AI risk-management guidance |
| EU AI Act | Risk-tiered regulatory expectations (general-purpose, limited, high-risk, prohibited) |
| POPIA & GDPR | Lawful, fair, and minimal processing of personal information |
| Responsible AI best practices | Industry-developed norms (e.g. Microsoft RAI standard, Partnership on AI, Singapore Model AI Governance) |

We track changes to each of these continuously and update our internal control mapping when a framework evolves.

## 3. How We Operationalise Responsible AI

| Practice | Where you can see it |
| --- | --- |
| Human-in-the-Loop on every consequential output | [HITL Governance Policy](/hitl-governance) |
| Published model cards | NAVI (Claude Sonnet 4.5) card, covering training-data lineage, intended use, out-of-scope use, evaluation dimensions, and known limitations |
| Indicator catalogue is public | The 30+ scam indicators used by NAVI Trust Intelligence are visible to anyone who interacts with the platform |
| Versioned scoring + prompts | Every output is stamped with the scoring-model version and prompt version. Regulators can interrogate any past assessment |
| Audit trails | User interactions, AI-generated outputs, human approvals, overrides, escalations and monitoring logs are retained where feasible to support accountability |
| Privacy-aligned data handling | See our [Privacy Policy](/privacy-policy): submitted content is not linked to identifiable users in anonymous-by-default surfaces like Trust Lens |
| Disclaimers on every output | Every NAVI output includes "AI-assisted assessment, not a final determination of fraud. Verify with the relevant regulator before acting." |
| No surveillance, no scraping, no dark patterns | We earn data through explicit opt-in submissions only |

## 4. What Responsible AI Means For Different Stakeholders

### For users and the public

You will always be told when you are interacting with AI. You will see what the AI found, why it found it, and where it might be wrong. You can withdraw consent and erase your on-device data at any time on apps that store it locally. You will never be presented with an AI output as if it were a human professional opinion.

### For institutional clients (banks, regulators, ombud schemes, fintechs)

You receive a governance-grade product: explainable scoring, audit trails, HITL workflows, override mechanisms, and the right to challenge any output. Our platform is built so that your compliance team can defend every AI-informed decision in front of a supervisor.

### For regulators

We publish methodology, model cards, indicator catalogues and our HITL Governance Policy proactively. We participate in the Global Regulatory TechSprint and engage with national supervisors on consumer-protection and trust-intelligence use cases. Our systems are designed to be interrogable by a non-technical regulator, not just by an ML engineer.

### For our employees, contractors and partners

Our internal AI Governance Office reviews every new model, prompt, and indicator change. Responsible AI training is mandatory and recurring. Whistle-blower channels are open for raising concerns about AI behaviour, bias, or misuse without retaliation.

## 5. What We Will Not Do

There are things responsible operators of AI must refuse. Altara AI commits that we will not:

- Develop, sell, or operate AI systems for social scoring of natural persons.
- Develop AI for biometric mass surveillance of the public.
- Develop AI that manipulates individuals through deceptive techniques into decisions that harm them.
- Sell user content (including the text people paste into our analysers) to data brokers.
- Use submitted content to train third-party generative models without explicit, separately-collected consent.
- Misrepresent AI-generated outputs as professional legal, financial or regulatory advice.
- Operate AI systems in jurisdictions where our use case is unlawful or unsafe.

These commitments are reviewed quarterly and may only be expanded, never narrowed.

## 6. Advocacy and Public Engagement

Altara AI actively advocates for responsible AI adoption across Africa and beyond:

- **Public education**: we publish open educational content on AI-enabled fraud patterns at our Insights blog and the Sentinel Investor Education library.
- **Industry collaboration**: we work with banks, regulators, ombud schemes and consumer-protection bodies to share threat intelligence and build community-facing defences.
- **Policy contribution**: we submit comments on AI regulatory consultations (e.g. South Africa's Information Regulator AI guidance, the FSCA's market-conduct AI principles).
- **Open frameworks**: where we develop reusable governance artefacts (indicator catalogues, model-card templates, HITL workflows), we publish them under permissive terms so other operators can learn from them.
`;

const hitl = `## 1. Purpose

This Human-in-the-Loop ("**HITL**") Governance Policy establishes the governance principles, oversight mechanisms, and operational controls implemented by Altara AI to ensure that artificial intelligence systems remain subject to meaningful human oversight, accountability, ethical governance, and regulatory compliance.

Altara AI recognises that AI systems must augment, not replace, human judgment, particularly in environments involving governance, compliance, cybersecurity, fraud detection, risk assessments, investor protection, and regulatory decision-support.

This policy supports Altara AI's commitment to:

- Responsible AI
- Human accountability
- Consumer protection
- Fairness and transparency
- AI governance best practices
- Digital trust and ethical innovation

## 2. Scope

This policy applies to:

- All AI-powered systems, tools, modules, and services developed, deployed, operated, or managed by Altara AI
- Internal AI systems used by employees, contractors, advisors, and partners
- AI-assisted outputs provided to clients
- AI-generated risk assessments, summaries, alerts, recommendations, classifications, or trust scores
- AI governance and monitoring platforms operated by Altara AI

This includes, but is not limited to:

- AI risk assessments
- AI trust scoring
- Fraud detection systems
- Scam analysis engines
- Regulatory intelligence tools
- AI governance dashboards
- AI document analysis
- Consumer protection monitoring systems

## 3. Governance Principles

Altara AI adopts the following HITL governance principles:

### 3.1 Human Accountability

Humans remain ultimately accountable for decisions, interpretations, actions, and outcomes generated using AI-assisted systems.

AI systems shall not independently make legally binding, regulatory, disciplinary, or high-impact decisions without human review and approval.

### 3.2 Human Oversight

AI outputs must be reviewable, challengeable, and overrideable by authorised human personnel.

Human reviewers must have sufficient authority, competency, and contextual understanding to validate AI-generated outputs.

### 3.3 Human-in-Command

Altara AI maintains governance structures ensuring that humans retain operational control over:

- AI deployment
- AI model configuration
- Escalation decisions
- Approval workflows
- Risk thresholds
- Monitoring controls
- Incident responses

### 3.4 Transparency

Users must be informed when they are interacting with or receiving outputs from AI-assisted systems.

AI-generated outputs must not be represented as definitive legal, regulatory, or professional advice unless independently reviewed and approved by appropriately qualified professionals.

### 3.5 Proportionality

The degree of human oversight shall be proportionate to:

- Risk level
- Regulatory impact
- Consumer impact
- Operational criticality
- Sensitivity of decisions
- Potential harm

Higher-risk use cases require enhanced human validation and governance controls.

## 4. Human Oversight Controls

Altara AI implements the following controls:

### 4.1 Review and Validation

AI-generated outputs may require:

- Human review
- Secondary approval
- Expert escalation
- Regulatory validation
- Compliance sign-off

depending on the risk classification of the activity.

### 4.2 Override Mechanisms

Authorised personnel may:

- Reject AI outputs
- Amend recommendations
- Escalate findings
- Suspend AI-driven processes
- Override automated classifications

where appropriate.

### 4.3 Escalation Procedures

Material risks, anomalies, or high-risk AI outputs must be escalated to designated governance personnel, including:

- Compliance officers
- AI governance leads
- Legal advisors
- Cybersecurity personnel
- Risk management teams

### 4.4 Auditability

Altara AI maintains audit trails where feasible, including:

- User interactions
- AI-generated outputs
- Human approvals
- Escalations
- Overrides
- Monitoring logs

to support accountability, transparency, and regulatory oversight.

## 5. AI Limitations and Disclaimers

AI systems operated by Altara AI:

- May produce inaccurate, incomplete, biased, or outdated outputs
- Operate probabilistically and not deterministically
- Should not be relied upon as the sole basis for decision-making
- Do not replace qualified legal, compliance, cybersecurity, financial, or regulatory professionals

Users remain responsible for independently validating outputs before reliance or implementation.

## 6. High-Risk Use Cases

Enhanced HITL governance applies to AI systems involving:

- Consumer protection
- Fraud detection
- Investor education
- Regulatory compliance
- Identity verification
- Trust scoring
- AI risk assessments
- Cybersecurity monitoring
- AI governance diagnostics

Such systems may require:

- Mandatory human validation
- Escalation workflows
- Governance approvals
- Enhanced monitoring
- Continuous assurance reviews

## 7. AI Governance Structures

Altara AI may establish governance structures including:

- AI Governance Councils
- Advisory Boards
- Ethics Committees
- Compliance Oversight Forums
- Model Risk Review Committees

to support responsible AI governance and oversight.

## 8. Monitoring and Continuous Improvement

Altara AI continuously monitors AI systems for:

- Performance degradation
- Hallucinations
- Bias indicators
- Emerging threats
- Adversarial manipulation
- False positives and false negatives
- Consumer harm risks

Governance controls may be updated periodically in response to:

- Regulatory developments
- Industry standards
- Emerging risks
- Operational findings
- Incident reviews

## 9. Regulatory Alignment

This policy is informed by evolving global AI governance principles and frameworks, including:

- OECD AI Principles
- NIST AI Risk Management Framework
- ISO/IEC AI governance standards
- EU AI Act principles
- Data protection and privacy laws
- Responsible AI best practices

## 10. Policy Review

This policy shall be reviewed periodically to ensure continued alignment with:

- Regulatory expectations
- Industry practices
- Operational realities
- Technological developments
- Organisational governance objectives

## 11. Contact

For questions relating to this policy or AI governance practices, contact:

${CONTACT}`;

const terms = `Welcome to Altara AI. By accessing or using our websites, platforms, applications, AI systems, services, or content ("**Services**"), you agree to these Terms of Use.

If you do not agree to these Terms, you must discontinue use of the Services.

## 1. About Altara AI

Altara AI is a digital trust and AI governance company focused on responsible AI, governance infrastructure, AI risk management, regulatory intelligence, and consumer protection technologies.

Our platforms may incorporate artificial intelligence, machine learning, analytics, automation, and human oversight mechanisms.

## 2. No Professional Advice

The Services, including AI-generated outputs, summaries, trust indicators, recommendations, alerts, or risk assessments, are provided for informational and assistive purposes only.

Altara AI does not provide legal, regulatory, financial, cybersecurity, investment, or professional advice unless expressly agreed in writing.

Users remain solely responsible for independently validating outputs and obtaining appropriate professional advice where required.

## 3. Human-in-the-Loop Governance

Altara AI applies Human-in-the-Loop ("**HITL**") governance principles.

AI systems are designed to support, not replace, human judgment.

AI-generated outputs may:

- contain inaccuracies,
- be incomplete,
- reflect probabilistic analysis,
- produce false positives or false negatives,
- or require further validation.

Users must not rely solely on AI-generated outputs for high-impact decisions.

## 4. Acceptable Use

Users agree not to:

- misuse the Services,
- upload unlawful or harmful content,
- attempt to reverse engineer systems,
- interfere with platform security,
- use the Services for fraudulent or illegal purposes,
- deploy automated abuse tools,
- or misuse AI-generated outputs.

Altara AI reserves the right to suspend or terminate access where misuse is suspected.

## 5. User Content

Users may upload documents, URLs, text, images, or other information for AI-assisted processing.

Users warrant that they:

- own the necessary rights,
- have lawful authority,
- and are permitted to provide such information.

Users remain responsible for the legality and accuracy of uploaded information.

## 6. Intellectual Property

All intellectual property relating to Altara AI, including:

- software,
- branding,
- frameworks,
- methodologies,
- AI governance models,
- trust scoring logic,
- platform architecture,
- graphics,
- and proprietary systems

remain the property of Altara AI or its licensors.

Users may not reproduce, reverse engineer, distribute, or commercially exploit any portion of the Services without prior written consent.

## 7. AI Limitations

Artificial intelligence systems are inherently probabilistic and may not always produce accurate or reliable results.

Altara AI makes no warranty that:

- outputs will be error-free,
- systems will operate uninterrupted,
- fraud or scams will always be detected,
- or AI-generated assessments will be accurate in all circumstances.

AI-generated trust indicators, fraud alerts, governance assessments, and risk scores should be treated as assistive indicators only.

## 8. Privacy

Use of the Services is subject to the Altara AI [Privacy Policy](/privacy-policy).

Users acknowledge that cloud infrastructure and third-party providers may be used to process and store information.

## 9. Third-Party Services

The Services may integrate with or reference third-party technologies, APIs, websites, or services.

Altara AI is not responsible for the content, accuracy, security, or practices of third-party providers.

## 10. Limitation of Liability

To the maximum extent permitted by law, Altara AI shall not be liable for:

- indirect or consequential damages,
- loss of profits,
- reputational harm,
- data loss,
- business interruption,
- or reliance on AI-generated outputs.

Use of the Services is at the user's own risk.

## 11. Service Availability

Altara AI does not guarantee uninterrupted or continuous availability of the Services.

Services may be modified, suspended, updated, or discontinued at any time.

## 12. Regulatory and Geographic Limitations

Certain Services may not be suitable for use in all jurisdictions or regulatory environments.

Users are responsible for ensuring lawful use within their jurisdiction.

## 13. Modifications

Altara AI may update these Terms periodically.

Continued use of the Services following updates constitutes acceptance of revised Terms.

## 14. Governing Law

These Terms shall be governed by the laws of the Republic of South Africa unless otherwise agreed in writing.

## 15. Contact

${CONTACT}`;

const privacy = `## 1. Introduction

Altara AI respects your privacy and is committed to protecting personal information in accordance with applicable privacy, data protection, and information governance laws and principles.

This Privacy Policy explains how Altara AI collects, uses, processes, stores, and protects information when users interact with our websites, platforms, AI systems, products, and services.

By using Altara AI services, you acknowledge and agree to the practices described in this Privacy Policy.

## 2. Information We Collect

Altara AI may collect:

### 2.1 Personal Information

Including:

- Name and surname
- Contact information
- Email address
- Organisation details
- User account information
- Communications and submissions

### 2.2 Technical Information

Including:

- IP addresses
- Browser information
- Device information
- Usage analytics
- Cookies and session data
- Platform interaction logs

### 2.3 Uploaded Content

Users may upload:

- Documents
- URLs
- Images
- Reports
- Screenshots
- Risk assessment information
- Compliance information

for AI-assisted analysis.

## 3. Use of AI Systems

Altara AI uses AI-assisted technologies to:

- Analyse documents and information
- Generate summaries
- Produce trust indicators
- Detect potential scams or fraud risks
- Support governance assessments
- Generate AI-assisted recommendations

AI outputs are probabilistic and may not always be accurate. Users must independently validate outputs before reliance or implementation.

## 4. Human Oversight

Altara AI follows Human-in-the-Loop governance principles.

AI systems are intended to support, not replace, human judgment.

AI-generated outputs may be reviewed, monitored, validated, or overridden by authorised personnel where appropriate.

## 5. How We Use Information

We may use information to:

- Provide services
- Operate AI systems
- Improve platform functionality
- Enhance security
- Detect fraud and abuse
- Conduct analytics
- Respond to enquiries
- Support compliance and governance activities
- Improve AI model performance where legally permissible

## 6. Legal Basis for Processing

Where applicable, Altara AI processes information based on:

- User consent
- Legitimate business interests
- Contractual necessity
- Legal obligations
- Fraud prevention and security purposes

## 7. Data Sharing

Altara AI does not sell personal information.

Information may be shared with:

- Service providers
- Cloud hosting providers
- Technology partners
- Professional advisors
- Regulators or authorities where legally required

subject to appropriate safeguards.

## 8. Data Hosting and International Transfers

Altara AI utilises cloud infrastructure and technology services provided by Amazon Web Services (AWS) and other approved service providers to host, process, store, and secure data.

Depending on the nature of the services provided and the geographic configuration of infrastructure environments, information submitted to Altara AI platforms may be processed or stored in data centres located in multiple jurisdictions, including jurisdictions outside the user's country of residence.

Altara AI seeks, where reasonably practicable, to utilise reputable cloud providers and implement appropriate technical and organisational safeguards designed to protect information during storage, transmission, and processing.

Cross-border data transfers may occur for purposes including:

- cloud hosting,
- backup and disaster recovery,
- cybersecurity monitoring,
- infrastructure resilience,
- technical support,
- platform operations,
- AI processing services,
- and systems administration.

Altara AI does not guarantee that data will remain exclusively within a single jurisdiction unless expressly agreed in writing as part of a dedicated enterprise or regulated hosting arrangement.

Users acknowledge that the use of cloud-based services may result in international data transfers and processing across multiple geographic regions.

Where required, Altara AI may support regional hosting configurations, tenant isolation, data residency considerations, encryption controls, and client-specific governance requirements subject to commercial and technical feasibility.

## 9. Data Security

Altara AI implements reasonable technical and organisational safeguards to protect information against:

- Unauthorised access
- Loss
- Misuse
- Disclosure
- Alteration
- Destruction

However, no system can guarantee absolute security.

## 10. Retention

Information is retained only for as long as reasonably necessary for:

- Operational purposes
- Legal obligations
- Security purposes
- Governance and audit requirements
- Dispute resolution

## 11. Cookies and Analytics

Altara AI may use cookies and analytics technologies to:

- Improve user experience
- Monitor platform performance
- Understand usage patterns
- Enhance security and functionality

Users may manage cookie preferences through browser settings where applicable.

## 12. User Responsibilities

Users must ensure that:

- Uploaded information is lawful
- They have appropriate authority to upload documents
- Sensitive information is handled responsibly
- AI outputs are independently validated before reliance

## 13. Third-Party Services

Altara AI platforms may integrate with third-party technologies or services.

Altara AI is not responsible for the privacy practices of third-party platforms or external websites.

## 14. Your Rights

Subject to applicable law, users may have rights relating to:

- Access
- Correction
- Deletion
- Objection
- Restriction of processing
- Data portability

Requests may be submitted through the contact details below.

## 15. Policy Updates

This Privacy Policy may be updated periodically to reflect:

- Regulatory developments
- Technology changes
- Operational requirements
- AI governance enhancements

Updated versions will be published on applicable platforms.

## 16. Contact

${CONTACT}`;

const OWNER = 'Altara AI · Part of the Navigate Compliance Group (Pty) Ltd';

export const POLICIES = {
  'privacy-policy': {
    title: 'Privacy Policy',
    meta: [['Effective Date', '27 May 2026'], ['Version', '1.0'], ['Owner', OWNER]],
    body: privacy,
  },
  'terms-of-use': {
    title: 'Terms of Use',
    meta: [['Effective Date', '28 May 2026'], ['Version', '1.0'], ['Owner', OWNER]],
    body: terms,
  },
  'hitl-governance': {
    title: 'HITL Governance',
    meta: [['Version', '1.0'], ['Effective Date', '1 March 2026'], ['Owner', 'Altara AI Governance Office'],
      ['Classification', 'Internal Governance Policy'], ['Issued by', OWNER]],
    body: hitl,
  },
  'responsible-ai': {
    title: 'Responsible AI',
    meta: [['Effective Date', '28 May 2026'], ['Version', '1.0'], ['Owner', 'Altara AI Governance Office'], ['Issued by', OWNER]],
    body: responsibleAi,
    hero: {
      eyebrow: 'Responsible AI · Altara AI',
      headline: ['AI must earn the trust', 'of the people it serves.'],
      intro: 'Responsible AI is not a label we use; it is the operating principle that shapes every model, every workflow, every release decision, and every line of policy at Altara.',
      badges: ['Effective 28 May 2026', 'v1.0', 'Public commitment'],
      contactEmail: 'hello@navigatelearning.io',
      commitments: [
        ['Human accountability', 'A person is always accountable for any decision informed by Altara AI.'],
        ['Transparency', 'We disclose when AI is used. Every output carries its model + prompt version.'],
        ['Explainability', 'No opaque scores. Every NAVI verdict shows the indicators and the evidence.'],
        ['Fairness', 'We test for bias across languages, demographics and regions, and disclose limitations.'],
        ['Safety & robustness', 'Continuous monitoring for hallucination, drift and adversarial manipulation.'],
        ['Privacy by design', 'Minimum data, encryption-by-default, user-rights controls: designed in, not bolted on.'],
      ],
      frameworks: ['OECD AI Principles', 'NIST AI RMF 1.0', 'ISO/IEC 42001', 'ISO/IEC 23894', 'EU AI Act', 'POPIA', 'GDPR'],
    },
  },
};

export const POLICY_LINKS = [
  { slug: 'responsible-ai', label: 'Responsible AI' },
  { slug: 'privacy-policy', label: 'Privacy Policy' },
  { slug: 'terms-of-use', label: 'Terms of Use' },
  { slug: 'hitl-governance', label: 'HITL Governance' },
];
